import { useEffect, useState, useCallback, useRef } from "react";

const PAGE_SIZE = 20;

export function useChatHistories(API_URL, userId, refreshTrigger) {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const fetchCountRef = useRef(0);

  const fetchInitial = useCallback(async () => {
    if (!API_URL || !userId) return;
    const fetchId = ++fetchCountRef.current;
    setLoading(true);
    setError(null);
    offsetRef.current = 0;

    const MAX_RETRIES = 2;
    let lastError;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (fetchId !== fetchCountRef.current) return;
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 2000 * attempt));
          if (fetchId !== fetchCountRef.current) return;
        }
        const res = await fetch(
          `${API_URL}/list_chat_histories?limit=${PAGE_SIZE}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          const err = new Error(`HTTP ${res.status}`);
          if (res.status >= 500 && attempt < MAX_RETRIES) {
            lastError = err;
            continue;
          }
          throw err;
        }
        const json = await res.json();
        if (fetchId !== fetchCountRef.current) return;
        const fetched = Array.isArray(json?.histories) ? json.histories : [];
        setHistories(fetched);
        setHasMore(fetched.length === PAGE_SIZE);
        offsetRef.current = fetched.length;
        setLoading(false);
        return;
      } catch (e) {
        lastError = e;
      }
    }

    if (fetchId !== fetchCountRef.current) return;
    setError(lastError);
    setLoading(false);
  }, [API_URL, userId]);

  const fetchMore = useCallback(async () => {
    if (!API_URL || !userId || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/list_chat_histories?limit=${PAGE_SIZE}&offset=${offsetRef.current}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const fetched = Array.isArray(json?.histories) ? json.histories : [];
      setHistories((prev) => [...prev, ...fetched]);
      setHasMore(fetched.length === PAGE_SIZE);
      offsetRef.current += fetched.length;
    } catch (e) {
      setError(e);
    } finally {
      setLoadingMore(false);
    }
  }, [API_URL, userId, loadingMore]);

  useEffect(() => {
    if (userId) {
      fetchInitial();
    } else {
      setHistories([]);
      setHasMore(true);
      offsetRef.current = 0;
    }
  }, [userId, fetchInitial, refreshTrigger]);

  return {
    histories,
    loading,
    loadingMore,
    error,
    hasMore,
    fetchMore,
    refresh: fetchInitial,
  };
}
